import React, { useState, useEffect } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import {
  X,
  Package,
  Check,
  CreditCard,
  AlertCircle,
} from "lucide-react";
import {
  membershipPackagesAPI,
  studentSubscriptionsAPI,
  sessionTypesAPI,
  notificationsAPI,
} from "../../services/api";
import LoadingSpinner from "../ui/LoadingSpinner";
import { useAuth } from "../../App";
import { useTranslation } from 'react-i18next';

const StudentPackageAssignmentModal = ({
  isOpen,
  onClose,
  student,
  organizationId,
  onAssignmentComplete,
}) => {
  const { colors, isDark } = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [selectedSessionTypeId, setSelectedSessionTypeId] = useState("all");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    startDate: new Date().toISOString().split("T")[0],
    paidAmount: "",
    paymentMethod: "cash",
    notes: "",
  });

  const paymentMethods = [
    { value: "cash", label: t('paymentMethods.cash') || "Nakit" },
    { value: "card", label: t('paymentMethods.card') || "Kredi/Banka Kartı" },
    { value: "bank_transfer", label: t('paymentMethods.bankTransfer') || "Banka Transferi" },
    { value: "cheque", label: t('paymentMethods.cheque') || "Çek" },
  ];

  useEffect(() => {
    if (isOpen && organizationId) {
      fetchPackages();
      fetchSessionTypes();
      resetForm();
    }
  }, [isOpen, organizationId]);

  const resetForm = () => {
    setStep(1);
    setSelectedPackage(null);
    setSelectedSessionTypeId("all");
    setErrors({});
    setFormData({
      startDate: new Date().toISOString().split("T")[0],
      paidAmount: "0",
      paymentMethod: "cash",
      notes: "",
    });
  };

  const fetchSessionTypes = async () => {
    try {
      const response = await sessionTypesAPI.getAllByOrg(organizationId);
      setSessionTypes(response.data.data || []);
    } catch (error) {
      console.error("Error fetching session types:", error);
    }
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await membershipPackagesAPI.getAllByOrg(organizationId);
      const availablePackages = response.data.data || [];

      const activePackages = availablePackages.filter(
        (pkg) => pkg.status === "active",
      );
      setPackages(activePackages);
      setErrors({});
    } catch (err) {
      console.error("Error fetching packages:", err);
      setErrors({ fetch: t('packageAssignment.noPackagesDesc') });
    } finally {
      setLoading(false);
    }
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setFormData((prev) => ({ ...prev, paidAmount: "0" }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validatePaymentStep = () => {
    const newErrors = {};
    if (!formData.startDate)
      newErrors.startDate = t('common.requiredField') || "Required field";
    if (parseFloat(formData.paidAmount) < 0)
      newErrors.paidAmount = t('common.negativeAmount') || "Amount cannot be negative";
    if (
      parseFloat(formData.paidAmount) > parseFloat(selectedPackage?.price || 0)
    ) {
      newErrors.paidAmount = t('common.exceedsAmount') || "Amount exceeds package price";
    }
    return newErrors;
  };

  const handleNextStep = () => {
    if (!selectedPackage) {
      setErrors({ package: t('packageAssignment.selectPackage') });
      return;
    }
    setErrors({});
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validatePaymentStep();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);

    try {
      const subscriptionData = {
        membershipPackageId: selectedPackage.id,
        startDate: formData.startDate,
        paidAmount: parseFloat(formData.paidAmount || 0),
        paymentMethod: formData.paymentMethod,
        notes: formData.notes,
      };

      await studentSubscriptionsAPI.create(
        organizationId,
        student.id,
        subscriptionData,
      );

      // Frontend Notification
      try {
        await notificationsAPI.create(organizationId, {
          title: t('packageAssignment.notificationTitle'),
          body: t('packageAssignment.notificationBody', {
            studentName: `${student.name} ${student.surname}`,
            packageName: selectedPackage.name,
            creator: user?.name || user?.email || t('common.unknown')
          }),
          type: "student_package_update",
          receivers: [], // Backend handles defaults or leave empty
        });
      } catch (notifErr) {
        console.error("Notification failed:", notifErr);
      }

      onAssignmentComplete();
      onClose();
    } catch (error) {
      console.error("Error assigning package:", error);
      setErrors({ submit: t('packageAssignment.error') || "Paket atanamadı. Lütfen tekrar deneyin." });
    } finally {
      setSubmitting(false);
    }
  };

  const getPaymentStatus = () => {
    if (!formData.paidAmount || !selectedPackage) return "";
    const paid = parseFloat(formData.paidAmount);
    const total = parseFloat(selectedPackage.price);
    if (paid >= total) return t('packageAssignment.fullyPaid');
    if (paid > 0) return t('packageAssignment.partiallyPaid');
    return t('packageAssignment.unpaid');
  };

  const getRemainingAmount = () => {
    if (!formData.paidAmount || !selectedPackage) return 0;
    return Math.max(
      0,
      parseFloat(selectedPackage.price) - parseFloat(formData.paidAmount),
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        className={`
        relative w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col
        ${colors.cardBg} ${colors.cardBorder}
        rounded-xl shadow-2xl border
        transform transition-all duration-200 ease-out scale-100
      `}
      >
        {/* Header */}
        <div className={`p-8 border-b ${colors.border} bg-zinc-950/50 backdrop-blur-md shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-violet-600/10 rounded-2xl border border-violet-500/20">
                <Package className="w-6 h-6 text-violet-500" />
              </div>
              <div>
                <h3 className={`text-xl font-black ${colors.textPrimary} tracking-tight`}>
                  {t('packageAssignment.title', { name: `${student?.name} ${student?.surname}` })}
                </h3>
                <div className="flex items-center mt-1 space-x-2">
                  <span className={`text-xs font-bold uppercase tracking-widest ${colors.textTertiary}`}>
                    Adım {step} / 2
                  </span>
                  <span className="w-1 h-1 bg-zinc-700 rounded-full"></span>
                  <span className={`text-xs font-bold text-violet-500 uppercase tracking-widest`}>
                    {step === 1 ? t('packageAssignment.step1') : t('packageAssignment.step2')}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={submitting}
              className={`p-2 rounded-xl transition-all duration-200 ${colors.textSecondary} hover:bg-white/5 hover:text-white active:scale-90`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stepper (Minimalist) */}
        <div className={`px-8 pt-6 shrink-0`}>
          <div className="flex items-center space-x-4">
            <div className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${step >= 1 ? "bg-violet-600 shadow-[0_0_10px_rgba(124,58,237,0.3)]" : "bg-zinc-800"}`}></div>
            <div className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${step >= 2 ? "bg-violet-600 shadow-[0_0_10px_rgba(124,58,237,0.3)]" : "bg-zinc-800"}`}></div>
          </div>
        </div>

        {/* Content - Fixed Scrolling */}
        <div className="flex-1 overflow-y-auto p-8 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
          {/* Step 1: Package Selection */}
          {step === 1 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {/* Filter Section */}
              <div className="flex flex-col space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className={`text-base font-bold ${colors.textPrimary} tracking-tight`}>
                    {t('packageAssignment.selectPackage', 'Bir Paket Seçin')}
                  </h4>
                  <div className={`text-xs ${colors.textTertiary} font-medium`}>
                    {packages.length} {t('packageAssignment.packagesAvailable', 'paket bulundu')}
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedSessionTypeId("all")}
                    className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 border ${
                      selectedSessionTypeId === "all"
                      ? "bg-violet-600 text-white border-violet-500 shadow-lg shadow-violet-600/20"
                      : `${colors.bgTertiary} ${colors.textSecondary} border-white/5 hover:bg-white/5`
                    }`}
                  >
                    {t('packageAssignment.all', 'Tümü')}
                  </button>
                  {sessionTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setSelectedSessionTypeId(type.id)}
                      className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 border ${
                        selectedSessionTypeId === type.id
                        ? "bg-violet-600 text-white border-violet-500 shadow-lg shadow-violet-600/20"
                        : `${colors.bgTertiary} ${colors.textSecondary} border-white/5 hover:bg-white/5`
                      }`}
                    >
                      {type.name}
                    </button>
                  ))}
                </div>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                  <LoadingSpinner />
                  <p className={`text-sm ${colors.textTertiary} animate-pulse`}>{t('common.loadingPackages', 'Paketler yükleniyor...')}</p>
                </div>
              ) : errors.fetch ? (
                <div className="text-center py-20 bg-red-500/5 rounded-3xl border border-red-500/10">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <p className="text-red-500 font-bold">{errors.fetch}</p>
                </div>
              ) : packages.length === 0 ? (
                <div className={`text-center py-20 bg-white/5 rounded-3xl border border-white/5`}>
                  <Package className={`w-12 h-12 ${colors.textMuted} mx-auto mb-4 opacity-20`} />
                  <h3 className={`text-lg font-bold ${colors.textPrimary} mb-2`}>{t('packageAssignment.noPackages')}</h3>
                  <p className={`${colors.textSecondary} text-sm max-w-xs mx-auto`}>{t('packageAssignment.noPackagesDesc')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {packages
                    .filter(
                      (pkg) =>
                        selectedSessionTypeId === "all" ||
                        pkg.sessionTypeId === selectedSessionTypeId,
                    )
                    .map((pkg) => {
                      const isSelected = selectedPackage?.id === pkg.id;
                      return (
                        <div
                          key={pkg.id}
                          onClick={() => handlePackageSelect(pkg)}
                          className={`
                            relative p-6 rounded-[2rem] cursor-pointer transition-all duration-300 border-2
                            flex flex-col justify-between group
                            ${isSelected 
                              ? "border-violet-600 bg-violet-600/5 shadow-2xl shadow-black/40" 
                              : `border-white/5 ${colors.cardBg} hover:bg-white/5 hover:border-white/20`
                            }
                          `}
                        >
                          {/* Selection Indicator */}
                          <div className={`absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                            isSelected ? "bg-violet-600 border-violet-500 scale-110" : "border-white/10"
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white stroke-[4]" />}
                          </div>

                          <div className="mb-6">
                            <div className="flex items-start justify-between mb-4 pr-10">
                              <div>
                                <h5 className={`text-lg font-black tracking-tight leading-tight ${isSelected ? "text-white" : colors.textPrimary}`}>
                                  {pkg.name}
                                </h5>
                                {pkg.isPopular && (
                                  <span className="inline-block mt-2 px-2 py-0.5 bg-amber-500/10 text-amber-500 text-[9px] font-black uppercase tracking-widest rounded border border-amber-500/20">
                                    {t('packageAssignment.popular', 'Popüler')}
                                  </span>
                                )}
                              </div>
                            </div>

                            {user?.role !== 'trainer' && (
                              <div className="flex items-baseline space-x-1">
                                <span className={`text-2xl font-black ${isSelected ? "text-violet-400" : colors.textPrimary}`}>
                                  ₺{pkg.price}
                                </span>
                                <span className={`text-[10px] font-bold ${colors.textTertiary} uppercase tracking-wider`}>
                                  / {pkg.numberOfSessions} Seans
                                </span>
                              </div>
                            )}
                            {user?.role !== 'trainer' && (
                              <div className={`text-[11px] font-medium mt-1 ${colors.textTertiary}`}>
                                ₺{(pkg.price / pkg.numberOfSessions).toFixed(2)} / seans başı
                              </div>
                            )}
                          </div>

                          <div className={`grid grid-cols-3 gap-2 pt-5 border-t ${isSelected ? "border-violet-600/20" : "border-white/5"}`}>
                            <div className="flex flex-col">
                              <span className={`text-[10px] font-bold uppercase tracking-widest ${colors.textTertiary} mb-1`}>Seans</span>
                              <span className={`text-sm font-black ${isSelected ? "text-white" : colors.textPrimary}`}>{pkg.numberOfSessions}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className={`text-[10px] font-bold uppercase tracking-widest ${colors.textTertiary} mb-1`}>Gün</span>
                              <span className={`text-sm font-black ${isSelected ? "text-white" : colors.textPrimary}`}>{pkg.validityPeriod}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className={`text-[10px] font-bold uppercase tracking-widest ${colors.textTertiary} mb-1`}>İptal</span>
                              <span className={`text-sm font-black ${isSelected ? "text-white" : colors.textPrimary}`}>{pkg.allowedCancellations || 0}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
          {/* Step 2: Payment Details */}
          {step === 2 && selectedPackage && (
            <form onSubmit={handleSubmit} className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              {/* Selected Package Summary Card */}
              <div className="relative overflow-hidden p-6 rounded-[2rem] bg-violet-600/5 border border-violet-500/20 shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-violet-600 rounded-xl">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <h4 className={`text-lg font-black ${colors.textPrimary} tracking-tight`}>
                      {selectedPackage.name}
                    </h4>
                  </div>
                  {user?.role !== 'trainer' && (
                    <div className="text-right">
                      <div className={`text-2xl font-black ${colors.textPrimary}`}>
                        ₺{selectedPackage.price}
                      </div>
                      <div className={`text-[10px] font-bold ${colors.textTertiary} uppercase tracking-widest`}>Toplam Tutar</div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3 sm:gap-6 pt-6 border-t border-violet-600/10">
                  <div className="flex flex-col">
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${colors.textTertiary} mb-1`}>Seans Sayısı</span>
                    <span className={`text-base font-black ${colors.textPrimary}`}>{selectedPackage.numberOfSessions}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${colors.textTertiary} mb-1`}>Geçerlilik</span>
                    <span className={`text-base font-black ${colors.textPrimary}`}>{selectedPackage.validityPeriod} Gün</span>
                  </div>
                  <div className="flex flex-col">
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${colors.textTertiary} mb-1`}>İptal Hakkı</span>
                    <span className={`text-base font-black ${colors.textPrimary}`}>{selectedPackage.allowedCancellations || 0}</span>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className={`block text-[10px] font-black uppercase tracking-[0.2em] ${colors.textSecondary} mb-3`}>
                      {t('packageAssignment.startDate', 'Başlangıç Tarihi')} *
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      className={`w-full px-5 py-4 border-2 ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} rounded-2xl focus:outline-none focus:ring-4 focus:ring-violet-600/20 focus:border-violet-600 transition-all duration-300 shadow-sm font-bold ${errors.startDate ? "border-red-500/50" : ""}`}
                      disabled={submitting}
                    />
                    {errors.startDate && <p className="mt-2 text-xs font-bold text-red-500">{errors.startDate}</p>}
                  </div>

                  <div>
                    <label className={`block text-[10px] font-black uppercase tracking-[0.2em] ${colors.textSecondary} mb-3`}>
                      {t('packageAssignment.paymentMethod', 'Ödeme Yöntemi')}
                    </label>
                    <select
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleInputChange}
                      className={`w-full px-5 py-4 border-2 ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} rounded-2xl focus:outline-none focus:ring-4 focus:ring-violet-600/20 focus:border-violet-600 transition-all duration-300 shadow-sm font-bold cursor-pointer`}
                      disabled={submitting}
                    >
                      {paymentMethods.map((method) => (
                        <option key={method.value} value={method.value} className="bg-zinc-900">{method.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-6">
                  {user?.role !== 'trainer' && (
                    <div>
                      <label className={`block text-[10px] font-black uppercase tracking-[0.2em] ${colors.textSecondary} mb-3`}>
                        {t('packageAssignment.paidAmount', 'Alınan Ödeme')}
                      </label>
                      <div className="relative">
                        <span className="absolute left-5 top-1/2 -translate-y-1/2 text-xl font-black text-violet-500">₺</span>
                        <input
                          type="number"
                          name="paidAmount"
                          value={formData.paidAmount}
                          onChange={handleInputChange}
                          className={`w-full pl-10 pr-5 py-4 border-2 ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} rounded-2xl focus:outline-none focus:ring-4 focus:ring-violet-600/20 focus:border-violet-600 transition-all duration-300 shadow-sm text-xl font-black ${errors.paidAmount ? "border-red-500/50" : ""}`}
                          placeholder="0.00"
                          disabled={submitting}
                        />
                      </div>
                      {errors.paidAmount && <p className="mt-2 text-xs font-bold text-red-500">{errors.paidAmount}</p>}

                      <div className="mt-4 flex gap-2">
                        {[
                          { label: 'Ödenmedi', value: '0' },
                          { label: 'Yarısı', value: (parseFloat(selectedPackage.price) / 2).toString() },
                          { label: 'Tamamı', value: selectedPackage.price }
                        ].map((btn) => (
                          <button
                            key={btn.label}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, paidAmount: btn.value }))}
                            className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-all duration-200 ${
                              formData.paidAmount === btn.value
                              ? "bg-violet-600 border-violet-500 text-white shadow-lg shadow-violet-600/20"
                              : "border-white/5 text-zinc-500 hover:bg-white/5 hover:text-white"
                            }`}
                          >
                            {btn.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className={`block text-[10px] font-black uppercase tracking-[0.2em] ${colors.textSecondary} mb-3`}>
                      {t('packageAssignment.notesOptional', 'Not / Açıklama')}
                    </label>
                    <textarea
                      name="notes"
                      rows={3}
                      value={formData.notes}
                      onChange={handleInputChange}
                      className={`w-full px-5 py-4 border-2 ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} rounded-2xl focus:outline-none focus:ring-4 focus:ring-violet-600/20 focus:border-violet-600 transition-all duration-300 shadow-sm font-medium resize-none`}
                      placeholder="Müşteri talepleri, özel notlar..."
                      disabled={submitting}
                    />
                  </div>
                </div>
              </div>

              {/* Payment Summary Footer */}
              <div className="mt-8 p-8 rounded-[2.5rem] bg-zinc-950/60 border border-white/5 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-10 opacity-5 group-hover:opacity-10 transition-opacity">
                  <CreditCard className="w-32 h-32" />
                </div>
                
                <h4 className={`text-xs font-black uppercase tracking-[0.2em] ${colors.textTertiary} mb-6`}>
                  Ödeme Özeti
                </h4>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Paket Tutarı</span>
                    {user?.role !== 'trainer' && (
                      <span className={`text-lg font-black ${colors.textPrimary}`}>
                        ₺{selectedPackage.price}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Tahsil Edilen</span>
                    {user?.role !== 'trainer' && (
                      <span className="text-lg font-black text-emerald-500">
                        ₺{parseFloat(formData.paidAmount || 0).toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Kalan Bakiye</span>
                    {user?.role !== 'trainer' && (
                      <span className={`text-lg font-black ${getRemainingAmount() > 0 ? "text-red-500" : "text-emerald-500"}`}>
                        ₺{getRemainingAmount().toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Durum</span>
                    <span className={`text-xs font-black uppercase px-2 py-1 rounded inline-block w-fit mt-1 ${
                      getRemainingAmount() === 0 ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-500"
                    }`}>
                      {getPaymentStatus()}
                    </span>
                  </div>
                </div>
              </div>

              {errors.submit && (
                <div className="flex items-center p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                  <p className="text-sm font-bold text-red-500">{errors.submit}</p>
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer Actions */}
        <div className={`px-8 py-6 border-t ${colors.border} bg-zinc-950/20 backdrop-blur-sm shrink-0`}>
          <div className="flex items-center justify-between">
            {step === 1 ? (
              <>
                <button
                  onClick={onClose}
                  className={`px-6 py-3 text-xs font-black uppercase tracking-widest transition-all duration-200 ${colors.textSecondary} hover:text-white`}
                >
                  İptal
                </button>
                <button
                  onClick={handleNextStep}
                  disabled={!selectedPackage}
                  className="px-10 py-4 bg-violet-600 text-white rounded-[2rem] hover:bg-violet-500 transition-all duration-300 font-black text-xs uppercase tracking-widest shadow-2xl shadow-violet-600/40 active:scale-95 disabled:opacity-50"
                >
                  İleri: Ödeme Detayları
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setStep(1)}
                  disabled={submitting}
                  className={`px-6 py-3 flex items-center gap-2 text-xs font-black uppercase tracking-widest transition-all duration-200 ${colors.textSecondary} hover:text-white`}
                >
                  Geri Dön
                </button>
                <div className="flex items-center gap-4">
                  <button
                    onClick={onClose}
                    disabled={submitting}
                    className={`px-6 py-3 text-xs font-black uppercase tracking-widest transition-all duration-200 ${colors.textSecondary} hover:text-white`}
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-12 py-4 bg-violet-600 text-white rounded-[2rem] hover:bg-violet-500 transition-all duration-300 font-black text-xs uppercase tracking-widest shadow-2xl shadow-violet-600/40 active:scale-95 disabled:opacity-50 flex items-center gap-3"
                  >
                    {submitting ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span>İşleniyor...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 stroke-[3]" />
                        <span>Paketi Ata</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentPackageAssignmentModal;
